/**
 * Feedback component
 *
 * Description:
 * Displays feedback and evaluation results for a quiz or lesson, including score, feedback text, and retry option.
 *
 * Features:
 * - Shows user's review and score.
 * - Displays feedback text based on score ranges.
 * - Provides a button to retry the video/quiz.
 * - Uses Material-UI for UI elements.
 */
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import ReplayIcon from '@mui/icons-material/Replay';

import classes from './Feedback.module.css';

const Feedback = props => {
  const { t } = useTranslation(['lesson']);
  const { idCourse, idLesson } = useParams();
  const dataAcademy = useSelector(state => state.academyInfo);

  const courseId = idCourse || props.idCourse;
  const lessonId = idLesson || props.idLesson;

  const isMatchByParams =
    (dataAcademy?.data?.shortName === 'retoprepalince' ||
      window.location.pathname.includes('retoprepalince') ||
      window.location.href.includes('retoprepalince')) &&
    String(courseId) === '5837' &&
    String(lessonId) === '47777';

  const isMatchByUrl =
    window.location.pathname.includes(
      'retoprepalince/q/5837/47777'
    ) ||
    window.location.href.includes('retoprepalince/q/5837/47777');

  const hideFeedbackContainer = isMatchByParams || isMatchByUrl;

  return (
    <>
      {hideFeedbackContainer ? (
        <div className={classes.thankYouContainer}>
          <h2 className={classes.thankYouText}>
            Gracias por participar
          </h2>
        </div>
      ) : (
        <div className={classes.feedbackContainer}>
          <aside className={classes.leftSection}>
            {props.calificationData.autoEvaluate ? (
              <>
                <p className={classes.text}>{t('your_review')}</p>
                <p className={classes.text}>
                  {t('qualification')}:{' '}
                  <span className={classes.score}>
                    {props.evaluate.score}
                  </span>
                </p>
                {props.evaluate.score < 70 && (
                  <p className={classes.feedbackText}>
                    {t('not_approving')}
                  </p>
                )}
                {/* <p>{date}</p> */}
              </>
            ) : (
              <p className={classes.text}>{t('your_review')}</p>
            )}
          </aside>
          <aside className={classes.rightSection}>
            {props.calificationData.autoEvaluate && (
              <>
                <p className={classes.text}>Retroalimentación</p>
                <p className={classes.feedbackText}>
                  {props.evaluate.score <=
                  props.feedbackData.response.firstRange ? (
                    <span>
                      {props.feedbackData.response.firstRangeFeed}
                    </span>
                  ) : props.evaluate.score <=
                    props.feedbackData.response.secondRange ? (
                    <span>
                      {props.feedbackData.response.secondRangeFeed}
                    </span>
                  ) : props.evaluate.score <=
                    props.feedbackData.response.thirdRange ? (
                    <span>
                      {props.feedbackData.response.thirdRangeFeed}
                    </span>
                  ) : (
                    ''
                  )}
                </p>
              </>
            )}
          </aside>
        </div>
      )}
      {/* Custom prop: 'className' for button container styling */}
      <aside className={classes.btnsContainer}>
        <Button
          // Custom prop: 'onClick' passes returnVideo handler
          onClick={props.returnVideo}
          className={classes.nextBtn}
        >
          Regresar al video <ReplayIcon />
        </Button>
      </aside>
    </>
  );
};

Feedback.propTypes = {
  /**
   * Feedback data object containing response ranges and feedback text
   */
  feedbackData: PropTypes.object,
  /**
   * Calification data object containing evaluation flags
   */
  calificationData: PropTypes.object,
  /**
   * Evaluation object containing score
   */
  evaluate: PropTypes.object,
  /**
   * Object to handle view changes (not used directly here)
   */
  changeView: PropTypes.object,
  /**
   * Function to handle returning to the video/quiz
   */
  returnVideo: PropTypes.func,
  idCourse: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  idLesson: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

Feedback.defaultProps = {
  feedbackData: {},
  calificationData: {},
  evaluate: {},
  changeView: {},
  returnVideo: undefined,
  idCourse: undefined,
  idLesson: undefined
};

export default Feedback;
